import type { Type } from '@angular/core';
import type { Routes } from '@angular/router';
import { AppPathsMap } from '@core/enums/app-paths';
import { FullscreenLayoutComponent } from '@core/fullscreen-layout/fullscreen-layout.component';
import { LayoutComponent } from '@core/layout/layout.component';
import { authorizationGuard } from '@features/authorization/guards/authorization.guard';
import { authenticationGuard } from '@features/login/guards/authentication.guard';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { defineRoute, IconNameMap } from '@kerberos-compliance/compliance-shared-ui';

const featureRoutes: Routes = [
  defineRoute({
    key: 'kyc-check',
    path: AppPathsMap.KycCheck,
    loadChildren: async () => {
      const { kycRoutes } = await import('@features/work-item/kyc/kyc-routes');
      return kycRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.kycChecks.title', icon: IconNameMap.crosshairs, order: 2 },
    },
  }),
  defineRoute({
    key: 'organization',
    path: AppPathsMap.Organization,
    loadChildren: async () => {
      const { organizationRoutes } = await import('@features/organization/organization-routes');
      return organizationRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.organizations.title', icon: IconNameMap.puzzle, order: 4 },
    },
  }),
  defineRoute({
    key: 'user',
    path: AppPathsMap.User,
    loadChildren: async () => {
      const { userRoutes } = await import('@features/user/user-routes');
      return userRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.BasicUser],
      nav: { labelKey: 'users.title', icon: IconNameMap.userGroup, order: 5 },
    },
  }),
  defineRoute({
    key: 'academy',
    path: AppPathsMap.Academy,
    loadChildren: async () => {
      const { academyRoutes } = await import('@features/academy/academy-routes');
      return academyRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.academy.title', icon: IconNameMap.graduationCap, order: 3 },
    },
  }),
  defineRoute({
    key: 'risk-analysis',
    path: AppPathsMap.DeliveryTracker,
    loadChildren: async () => {
      const { deliveryTrackerRoutes } = await import('@features/delivery-tracker/delivery-tracker-routes');
      return deliveryTrackerRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.deliveryTracker.title', icon: IconNameMap.fileLines, order: 1 },
    },
  }),
  defineRoute({
    key: 'notifications',
    path: AppPathsMap.Notifications,
    loadChildren: async () => {
      const { emailNotificationRoutes } = await import('@features/email-notification/email-notification-routes');
      return emailNotificationRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.notifications.title', icon: IconNameMap.envelope, order: 6 },
    },
  }),
  defineRoute({
    key: 'document-library',
    path: AppPathsMap.DocumentLibrary,
    loadChildren: async () => {
      const { documentLibraryRoutes } = await import('@features/document-library/document-library-routes');
      return documentLibraryRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
      nav: { labelKey: 'navigation.documentLibrary.title', icon: IconNameMap.files, order: 7 },
    },
  }),
  defineRoute({
    key: 'dashboard',
    path: AppPathsMap.Dashboard,
    pathMatch: 'full',
    loadChildren: async () => {
      const { kycRoutes } = await import('@features/work-item/kyc/kyc-routes');
      return kycRoutes;
    },
    canActivate: [authorizationGuard],
    data: {
      roles: [NamedRoleMap.InternalUser],
    },
  }),
  {
    path: AppPathsMap.Root,
    pathMatch: 'full',
    redirectTo: AppPathsMap.KycCheck,
  },
];

export const routes: Routes = [
  defineRoute({
    key: 'login',
    path: AppPathsMap.Login,
    loadChildren: async () => {
      const { loginRoutes } = await import('@features/login/login.routes');
      return loginRoutes;
    },
  }),
  defineRoute({
    key: 'authCallback',
    path: AppPathsMap.AuthCallback,
    loadComponent: async () => {
      const { AuthCallbackComponent } = await import('@features/auth-callback/auth-callback.component');
      return AuthCallbackComponent;
    },
  }),
  defineRoute({
    key: 'impersonationReturn',
    path: AppPathsMap.ImpersonationReturn,
    loadComponent: async () => {
      const { ImpersonationReturnComponent } =
        await import('@features/authentication/components/impersonation-return.component');
      return ImpersonationReturnComponent;
    },
  }),
  defineRoute({
    key: 'maintenance',
    path: AppPathsMap.Maintenance,
    loadComponent: async () => {
      const { MaintenancePageComponent } =
        await import('@core/error-pages/maintenance-page/maintenance-page.component');
      return MaintenancePageComponent;
    },
  }),
  defineRoute({
    key: 'unauthorized',
    path: AppPathsMap.Unauthorized,
    loadComponent: async () => {
      const { UnauthorizedPageComponent } = await import('@core/error-pages/unauthorized/unauthorized-page.component');
      return UnauthorizedPageComponent;
    },
  }),
  defineRoute({
    key: 'logout',
    path: AppPathsMap.Logout,
    loadComponent: async () => {
      const { LogoutComponent } = await import('@features/logout/logout.component');
      return LogoutComponent;
    },
  }),
  defineRoute({
    key: 'fullscreen',
    path: AppPathsMap.Fullscreen,
    component: FullscreenLayoutComponent,
    children: [
      defineRoute({
        key: 'kyc-check.fullscreen',
        path: AppPathsMap.KycCheck,
        loadChildren: async () => {
          const { kycRoutes } = await import('@features/work-item/kyc/kyc-routes');
          return kycRoutes;
        },
        canActivate: [authorizationGuard],
        data: {
          roles: [NamedRoleMap.InternalUser],
        },
      }),
    ],
  }),
  defineRoute({
    key: 'not-found',
    path: AppPathsMap.Root,
    component: LayoutComponent,
    canActivate: [authenticationGuard],
    children: [...featureRoutes],
  }),
  {
    path: '**',
    loadComponent: async (): Promise<Type<unknown>> => {
      const { NotFoundComponent } = await import('@core/error-pages/not-found/not-found.component');
      return NotFoundComponent;
    },
  },
];
